/**
 * Screenshots Carousel Section
 * Full-width carousel with auto-play and keyboard navigation
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, PlayCircle, PauseCircle } from 'lucide-react';

interface Screenshot {
  id: string;
  title: string;
  description: string;
  imagePath: string;
}

const screenshots: Screenshot[] = [
  {
    id: 'playground',
    title: 'Interactive Playground',
    description: 'Test routing decisions in real-time with live model comparison',
    imagePath: '/screenshots/playground.png',
  },
  {
    id: 'analytics',
    title: 'Advanced Analytics Dashboard',
    description: 'Track costs, latency, and model performance across all requests',
    imagePath: '/screenshots/dashboard_analytics.png',
  },
  {
    id: 'roi',
    title: 'ROI Calculator',
    description: 'Forecast savings based on your usage patterns and model mix',
    imagePath: '/screenshots/roi.png',
  },
  {
    id: 'uptime',
    title: 'Provider Uptime Monitoring',
    description: 'Real-time health status for all AI providers with historical data',
    imagePath: '/screenshots/provider_uptime.png',
  },
  {
    id: 'training',
    title: 'ML Training Dashboard',
    description: 'Monitor training runs with real-time telemetry and epoch metrics',
    imagePath: '/screenshots/model_training.png',
  },
];

const AUTO_PLAY_INTERVAL = 5000; // 5 seconds

export function ScreenshotsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % screenshots.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  }, []);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || isPaused) return;

    const interval = setInterval(nextSlide, AUTO_PLAY_INTERVAL);

    return () => clearInterval(interval);
  }, [isAutoPlaying, isPaused, nextSlide]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      } else if (e.key === ' ') {
        e.preventDefault();
        setIsAutoPlaying((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);

  const currentScreenshot = screenshots[currentIndex];

  return (
    <section className="py-16 px-4 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            See Pathfinder in Action
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Explore our powerful dashboard and tools
          </p>
        </motion.div>

        {/* Carousel Container */}
        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Main Carousel */}
          <div className="relative aspect-video rounded-xl overflow-hidden bg-card border border-border shadow-2xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="absolute inset-0"
              >
                {/* Screenshot image */}
                <img
                  src={currentScreenshot.imagePath}
                  alt={currentScreenshot.title}
                  className="w-full h-full object-contain bg-muted/50"
                />

                {/* Caption overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/95 to-background/0 p-6">
                  <h3 className="text-xl font-bold mb-1">{currentScreenshot.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {currentScreenshot.description}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <div className="absolute inset-0 flex items-center justify-between p-4 pointer-events-none">
              <Button
                variant="ghost"
                size="icon"
                className="pointer-events-auto bg-background/80 hover:bg-background/90 backdrop-blur-sm"
                onClick={prevSlide}
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="pointer-events-auto bg-background/80 hover:bg-background/90 backdrop-blur-sm"
                onClick={nextSlide}
              >
                <ChevronRight className="w-6 h-6" />
              </Button>
            </div>

            {/* Auto-play Control */}
            <div className="absolute bottom-4 right-4">
              <Button
                variant="ghost"
                size="icon"
                className="bg-background/80 hover:bg-background/90 backdrop-blur-sm"
                onClick={() => setIsAutoPlaying((prev) => !prev)}
              >
                {isAutoPlaying ? (
                  <PauseCircle className="w-5 h-5" />
                ) : (
                  <PlayCircle className="w-5 h-5" />
                )}
              </Button>
            </div>
          </div>

          {/* Slide Indicators */}
          <div className="flex items-center justify-center space-x-2 mt-6">
            {screenshots.map((screenshot, index) => (
              <button
                key={screenshot.id}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? 'w-8 bg-primary'
                    : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Thumbnail Navigation */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-4 mt-8">
            {screenshots.map((screenshot, index) => (
              <button
                key={screenshot.id}
                onClick={() => goToSlide(index)}
                className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                  index === currentIndex
                    ? 'border-primary shadow-lg scale-105'
                    : 'border-transparent hover:border-muted-foreground/50'
                }`}
              >
                <img
                  src={screenshot.imagePath}
                  alt={screenshot.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-background/60 hover:bg-background/40 transition-colors flex items-center justify-center">
                  <p className="text-xs text-center px-2 font-medium">{screenshot.title}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Keyboard Hint */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            Use arrow keys to navigate • Space to pause/play
          </p>
        </div>
      </div>
    </section>
  );
}
